import * as React from "react";
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import Check from "@material-ui/icons/Check";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPost } from "../getConfig";
import CustomConfirm from "ra-custom-confirm";

const useStyles = makeStyles((theme) => ({
  main: {
    padding: 24,
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    marginBottom: 24,
  },
  tabsPaper: {
    marginBottom: 16,
    borderRadius: 8,
  },
  searchPaper: {
    padding: 20,
    marginBottom: 16,
    borderRadius: 8,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  card: {
    marginBottom: 16,
    borderRadius: 8,
  },
  statusChip: {
    fontWeight: 600,
  },
  statBox: {
    textAlign: "center",
    padding: 16,
    borderRadius: 8,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
  },
  statLabel: {
    fontSize: 12,
    color: "#666",
    marginTop: 4,
  },
  formRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginTop: 16,
  },
  tableHead: {
    backgroundColor: "#f5f5f5",
  },
  uploadArea: {
    border: "2px dashed #ccc",
    borderRadius: 8,
    padding: 32,
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: "#e91e8c",
    },
  },
  uploadAreaActive: {
    borderColor: "#e91e8c",
    backgroundColor: "#fce4ec",
  },
  resultRow: {
    "&:nth-child(even)": {
      backgroundColor: "#fafafa",
    },
  },
}));

const SOURCE_TYPES = [
  { value: "campaign", label: "Campaign" },
  { value: "admin", label: "Admin" },
  { value: "survey", label: "Survey" },
  { value: "loyalty", label: "Loyalty" },
  { value: "activity", label: "Activity" },
];

// Boost source label mapping — used to render the boost_source column in the
// User Boost history table with human-friendly labels.
const BOOST_SOURCE_LABELS: Record<string, string> = {
  admin_grant: "Manual (admin)",
  admin_manual: "Manual (admin)",
  referral: "Referral",
  referral_v2_commission: "Referral",
  prize_draw: "Prize Draw",
  grocery_challenge: "Grocery Challenge",
  shopping_challenge: "Shopping Challenge",
  second_shop_challenge: "Second Shop",
  activity: "Activity",
  gift: "Gift",
  campaign: "Campaign",
};

const formatBoostSource = (src: string | null | undefined): string => {
  if (!src) return "—";
  return BOOST_SOURCE_LABELS[src] || src;
};

const formatDateTime = (val: string | Date | null | undefined): string => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (val: string | Date | null | undefined): string => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Parse a CSV string → array of email strings
function parseEmailsFromCsv(csvText: string): string[] {
  const lines = csvText.split(/\r?\n/).filter((l) => l.trim());
  const emails: string[] = [];

  for (const line of lines) {
    // Support: single column (just email) or multi-column (email is first col)
    const firstCol = line.split(",")[0].trim().replace(/^"|"$/g, "");
    // Skip header row
    if (firstCol.toLowerCase() === "email") continue;
    if (firstCol.includes("@")) emails.push(firstCol);
  }

  return Array.from(new Set(emails)); // dedupe
}

const BoostAward = () => {
  const classes = useStyles();
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState(0);

  // ── Single user state ──────────────────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [awarding, setAwarding] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [weeks, setWeeks] = useState(1);
  const [sourceType, setSourceType] = useState("campaign");
  const [sourceId, setSourceId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Bulk state ─────────────────────────────────────────────────────────────
  const [bulkEmails, setBulkEmails] = useState<string[]>([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkWeeks, setBulkWeeks] = useState(1);
  const [bulkSourceType, setBulkSourceType] = useState("campaign");
  const [bulkSourceId, setBulkSourceId] = useState("");
  const [bulkAwarding, setBulkAwarding] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<any>(null);
  const [dragging, setDragging] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobState, setJobState] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any>(null);
  const pollRef = useRef<any>(null);

  // Poll job status every 3s while active
  useEffect(() => {
    if (!jobId || jobState === "completed" || jobState === "failed") return;
    pollRef.current = setInterval(async () => {
      try {
        const config = getConfigGet({});
        const res: any = await axios.get(
          `${API}/admin/bulk-award-boost?job_id=${jobId}`,
          config
        );
        const d = res?.data;
        if (d?.status) {
          setJobState(d.state);
          setJobProgress(d.progress);
          if (d.state === "completed") {
            setBulkResult(d.result);
            clearInterval(pollRef.current);
          } else if (d.state === "failed") {
            clearInterval(pollRef.current);
          }
        }
      } catch (_) {}
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [jobId, jobState]);

  // ── Single user handlers ───────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!email.trim()) {
      notify("Please enter an email", "error");
      return;
    }
    setLoading(true);
    setUserData(null);
    try {
      const config = getConfigGet({});
      const res: any = await axios.get(
        `${API}/admin/referral-v2-search?query=${encodeURIComponent(email.trim())}`,
        config
      );
      if (!res?.data?.status) {
        notify(res?.data?.message || "User not found", "error");
        return;
      }
      const data = res.data.data;
      if (!data?.user) { notify("User not found", "error"); return; }
      setUserData(data);
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to search user", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAwardClick = () => {
    if (!userData?.user?.id) { notify("No user selected", "error"); return; }
    setConfirmOpen(true);
  };

  const handleConfirmAward = async () => {
    setConfirmOpen(false);
    setAwarding(true);
    try {
      const headers = getConfigPost();
      const payload = {
        user_id: userData.user.id,
        weeks,
        source_type: sourceType,
        source_id: sourceId || undefined,
        award_type: "immediate",
      };
      const res: any = await axios.post(API + "/boost-award", payload, { headers });
      if (res?.data?.status) {
        notify("Boost awarded successfully!", "info", "", undefined, 5000);
        handleSearch();
      } else {
        notify(res?.data?.message || "Failed to award boost", "error");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to award boost", "error");
    } finally {
      setAwarding(false);
    }
  };

  // ── Bulk handlers ──────────────────────────────────────────────────────────
  const handleFileChange = (file: File) => {
    if (!file) return;
    setBulkFileName(file.name);
    setBulkResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseEmailsFromCsv(text);
      setBulkEmails(parsed);
      if (parsed.length === 0) {
        notify("No valid emails found in CSV", "warning");
      } else {
        notify(`Loaded ${parsed.length} email(s) from CSV`, "info");
      }
    };
    reader.readAsText(file);
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileChange(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange(file);
  };

  const handleBulkAwardClick = () => {
    if (bulkEmails.length === 0) {
      notify("Please upload a CSV with at least one email", "warning");
      return;
    }
    setBulkConfirmOpen(true);
  };

  const handleConfirmBulkAward = async () => {
    setBulkConfirmOpen(false);
    setBulkAwarding(true);
    setBulkResult(null);
    setJobId(null);
    setJobState(null);
    setJobProgress(null);
    try {
      const headers = getConfigPost();
      const payload = {
        emails: bulkEmails,
        weeks: bulkWeeks,
        source_id: bulkSourceId || undefined,
        award_type: "immediate",
      };
      const res: any = await axios.post(
        `${API}/admin/bulk-award-boost`,
        payload,
        { headers }
      );
      if (res?.data?.status && res?.data?.job_id) {
        setJobId(res.data.job_id);
        setJobState("waiting");
        notify(`Job queued for ${res.data.total} users. Processing in background...`, "info", "", undefined, 5000);
      } else {
        notify(res?.data?.message || "Failed to enqueue bulk award", "error");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Bulk award failed", "error");
    } finally {
      setBulkAwarding(false);
    }
  };

  const userStats = userData?.stats;
  const user = userData?.user;

  return (
    <div className={classes.main}>
      <Typography className={classes.title}>User Boost</Typography>
      <Typography className={classes.subtitle}>
        View a user's current boost, boost history, and award new boosts
      </Typography>

      {/* Mode Tabs */}
      <Paper className={classes.tabsPaper} elevation={1}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Single User" />
          <Tab label="Bulk CSV" />
        </Tabs>
      </Paper>

      {/* ── SINGLE USER TAB ── */}
      {activeTab === 0 && (
        <>
          <Paper className={classes.searchPaper} elevation={1}>
            <div className={classes.searchRow}>
              <TextField
                label="User Email"
                variant="outlined"
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                style={{ flex: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              >
                Search
              </Button>
            </div>
          </Paper>

          {userData && user && (
            <Card className={classes.card} elevation={1}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    <Typography variant="h6">
                      {user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || "(no name)"}
                    </Typography>
                    {userStats?.is_boost_active ? (
                      <Chip label="Boost Active" size="small" className={classes.statusChip}
                        style={{ backgroundColor: "#4caf50", color: "#fff" }} />
                    ) : (
                      <Chip label="No Active Boost" size="small" className={classes.statusChip}
                        style={{ backgroundColor: "#9e9e9e", color: "#fff" }} />
                    )}
                  </Box>
                }
                subheader={`${user.email} | ID: ${user.id}`}
              />
              <CardContent>
                {/* Top-level stats */}
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#e3f2fd" }}>
                      <Typography className={classes.statValue} style={{ color: "#1565c0" }}>
                        {userStats?.is_boost_active ? "Yes" : "No"}
                      </Typography>
                      <Typography className={classes.statLabel}>Boost Active</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                      <Typography className={classes.statValue} style={{ color: "#ff9800" }}>
                        {userStats?.boost_weeks_remaining || 0}w
                      </Typography>
                      <Typography className={classes.statLabel}>Weeks Remaining</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#e8f5e9" }}>
                      <Typography className={classes.statValue} style={{ color: "#2e7d32" }}>
                        {userStats?.successful_referrals || 0}
                      </Typography>
                      <Typography className={classes.statLabel}>Successful Referrals</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#fce4ec" }}>
                      <Typography className={classes.statValue} style={{ color: "#c62828" }}>
                        {userStats?.is_ambassador ? "Yes" : "No"}
                      </Typography>
                      <Typography className={classes.statLabel}>Ambassador</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Current boost window */}
                <Divider style={{ margin: "20px 0" }} />
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                  Current Boost Window
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#f3e5f5" }}>
                      <Typography style={{ fontSize: 18, fontWeight: 700, color: "#6a1b9a" }}>
                        {formatDateTime(userStats?.active_boost_start_date)}
                      </Typography>
                      <Typography className={classes.statLabel}>Start Date</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className={classes.statBox} style={{ backgroundColor: "#ede7f6" }}>
                      <Typography style={{ fontSize: 18, fontWeight: 700, color: "#4527a0" }}>
                        {formatDateTime(userStats?.active_boost_end_date)}
                      </Typography>
                      <Typography className={classes.statLabel}>End Date</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Boost history */}
                <Divider style={{ margin: "20px 0" }} />
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                  Boost History
                  {userData?.boost_history?.length ? ` (${userData.boost_history.length})` : ""}
                </Typography>
                {(!userData?.boost_history || userData.boost_history.length === 0) ? (
                  <Typography variant="body2" style={{ color: "#666", fontStyle: "italic" }}>
                    No boost history yet.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell><strong>Start</strong></TableCell>
                        <TableCell><strong>End</strong></TableCell>
                        <TableCell align="right"><strong>Weeks</strong></TableCell>
                        <TableCell><strong>Source</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userData.boost_history.map((row: any) => {
                        // Active if status='active' AND (no end date OR end date still in future).
                        // Some boost sources may leave boost_end_date null for
                        // indefinite/admin-granted boosts — those must still
                        // render as Active, not fall through to grey "expired".
                        const isActive = String(row.status || "").toLowerCase() === "active"
                          && (!row.boost_end_date || new Date(row.boost_end_date).getTime() > Date.now());
                        return (
                          <TableRow key={row.id} className={classes.resultRow}>
                            <TableCell>{formatDate(row.boost_start_date)}</TableCell>
                            <TableCell>{formatDate(row.boost_end_date)}</TableCell>
                            <TableCell align="right">{row.weeks_added ?? "—"}</TableCell>
                            <TableCell>{formatBoostSource(row.boost_source)}</TableCell>
                            <TableCell>
                              <Chip
                                label={isActive ? "Active" : (row.status || "expired")}
                                size="small"
                                style={{
                                  backgroundColor: isActive ? "#4caf50" : "#9e9e9e",
                                  color: "#fff",
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {/* Award new boost — below history so ops can review context first */}
                <Divider style={{ margin: "20px 0" }} />
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Award New Boost
                </Typography>
                <div className={classes.formRow}>
                  <TextField
                    label="Weeks"
                    type="number"
                    variant="outlined"
                    size="small"
                    value={weeks}
                    onChange={(e) => setWeeks(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1, max: 10 }}
                    style={{ width: 100 }}
                  />
                  <TextField
                    label="Source Type"
                    variant="outlined"
                    size="small"
                    select
                    value={sourceType}
                    onChange={(e) => setSourceType(e.target.value)}
                    style={{ width: 180 }}
                  >
                    {SOURCE_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Source ID (optional)"
                    variant="outlined"
                    size="small"
                    value={sourceId}
                    onChange={(e) => setSourceId(e.target.value)}
                    placeholder="e.g. spring-2026-promo"
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAwardClick}
                    disabled={awarding}
                    startIcon={awarding ? <CircularProgress size={20} /> : <FlashOnIcon />}
                  >
                    Award Boost
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ── BULK CSV TAB ── */}
      {activeTab === 1 && (
        <Card className={classes.card} elevation={1}>
          <CardContent>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
              Bulk Award Boost via CSV
            </Typography>

            {/* Sample CSV download */}
            <Box display="flex" justifyContent="flex-end" style={{ marginBottom: 8 }}>
              <Button
                size="small"
                onClick={() => {
                  const csv = "email\nuser1@example.com\nuser2@example.com\nuser3@example.com";
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "bulk-award-boost-sample.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                ↓ Download sample CSV
              </Button>
            </Box>

            {/* Upload area */}
            <Box
              className={`${classes.uploadArea} ${dragging ? classes.uploadAreaActive : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon style={{ fontSize: 40, color: "#aaa", marginBottom: 8 }} />
              <Typography variant="body1" style={{ color: "#666" }}>
                {bulkFileName
                  ? `${bulkFileName} — ${bulkEmails.length} email(s) loaded`
                  : "Drag & drop a CSV here, or click to browse"}
              </Typography>
              <Typography variant="caption" style={{ color: "#aaa" }}>
                CSV must have an email column (or one email per row)
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={onFileInputChange}
              />
            </Box>

            {/* Boost options */}
            <div className={classes.formRow}>
              <TextField
                label="Weeks"
                type="number"
                variant="outlined"
                size="small"
                value={bulkWeeks}
                onChange={(e) => setBulkWeeks(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: 10 }}
                style={{ width: 100 }}
              />
              <TextField
                label="Source Type"
                variant="outlined"
                size="small"
                select
                value={bulkSourceType}
                onChange={(e) => setBulkSourceType(e.target.value)}
                style={{ width: 180 }}
              >
                {SOURCE_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Source ID (optional)"
                variant="outlined"
                size="small"
                value={bulkSourceId}
                onChange={(e) => setBulkSourceId(e.target.value)}
                placeholder="e.g. spring-2026-promo"
                style={{ flex: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleBulkAwardClick}
                disabled={bulkAwarding || bulkEmails.length === 0}
                startIcon={bulkAwarding ? <CircularProgress size={20} /> : <FlashOnIcon />}
              >
                Bulk Award ({bulkEmails.length})
              </Button>
            </div>

            {/* Job progress */}
            {jobId && (
              <>
                <Divider style={{ margin: "20px 0" }} />
                <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 8 }}>
                  <Chip
                    label={jobState || "queued"}
                    size="small"
                    style={{
                      backgroundColor:
                        jobState === "completed" ? "#4caf50" :
                        jobState === "failed" ? "#f44336" :
                        jobState === "active" ? "#2196f3" : "#9e9e9e",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="body2" style={{ color: "#666" }}>
                    Job ID: {jobId}
                  </Typography>
                  {(jobState === "waiting" || jobState === "active") && (
                    <CircularProgress size={16} />
                  )}
                </Box>

                {jobProgress && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round((jobProgress.processed / jobProgress.total) * 100)}
                      style={{ marginBottom: 8, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" style={{ color: "#666" }}>
                      {jobProgress.processed} / {jobProgress.total} processed
                      {" · "}{jobProgress.succeeded} awarded
                      {" · "}{jobProgress.failed} failed
                    </Typography>
                  </>
                )}

                {/* Final results once complete */}
                {jobState === "completed" && bulkResult && (
                  <>
                    <Box display="flex" style={{ gap: 16, marginTop: 16, marginBottom: 16 }}>
                      <Box className={classes.statBox} style={{ backgroundColor: "#e8f5e9", flex: 1 }}>
                        <Typography className={classes.statValue} style={{ color: "#2e7d32" }}>
                          {bulkResult.succeeded}
                        </Typography>
                        <Typography className={classes.statLabel}>Awarded</Typography>
                      </Box>
                      <Box className={classes.statBox} style={{ backgroundColor: "#fce4ec", flex: 1 }}>
                        <Typography className={classes.statValue} style={{ color: "#c62828" }}>
                          {bulkResult.failed}
                        </Typography>
                        <Typography className={classes.statLabel}>Failed</Typography>
                      </Box>
                    </Box>

                    {bulkResult.failedEmails?.length > 0 && (
                      <>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                          Failed Emails
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow className={classes.tableHead}>
                              <TableCell><strong>Email</strong></TableCell>
                              <TableCell><strong>Reason</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {bulkResult.failedEmails.map((failEmail: string) => (
                              <TableRow key={failEmail} className={classes.resultRow}>
                                <TableCell>{failEmail}</TableCell>
                                <TableCell style={{ color: "#c62828" }}>
                                  {bulkResult.failedReasons?.[failEmail] || "Unknown error"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </>
                )}

                {jobState === "failed" && (
                  <Typography style={{ color: "#f44336", marginTop: 8 }}>
                    Job failed — check backend logs for details.
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Single user confirm */}
      <CustomConfirm
        isOpen={confirmOpen}
        title="Confirm Boost Award"
        user={`Award ${weeks} week(s) boost to ${user?.email || ""} (${user?.name || ""})?\n\nSource: ${sourceType}${sourceId ? ` (${sourceId})` : ""}\nThis will activate the boost immediately.`}
        content={CustomConfirmContent}
        confirm="Award Boost"
        confirmColor="primary"
        ConfirmIcon={Check}
        cancel="Cancel"
        CancelIcon={ErrorOutline}
        onConfirm={handleConfirmAward}
        onClose={() => setConfirmOpen(false)}
      />

      {/* Bulk confirm */}
      <CustomConfirm
        isOpen={bulkConfirmOpen}
        title="Confirm Bulk Boost Award"
        user={`Award ${bulkWeeks} week(s) boost to ${bulkEmails.length} user(s)?\n\nSource: ${bulkSourceType}${bulkSourceId ? ` (${bulkSourceId})` : ""}\nThis will activate boosts immediately for all valid users.`}
        content={CustomConfirmContent}
        confirm="Bulk Award"
        confirmColor="primary"
        ConfirmIcon={Check}
        cancel="Cancel"
        CancelIcon={ErrorOutline}
        onConfirm={handleConfirmBulkAward}
        onClose={() => setBulkConfirmOpen(false)}
      />

      <Notification />
    </div>
  );
};

const CustomConfirmContent = (props: any) => {
  const record = props.user;
  return (
    <Typography style={{ whiteSpace: "pre-line" }}>
      {typeof record === "string" ? record : JSON.stringify(record)}
    </Typography>
  );
};

export default BoostAward;
